import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Invitation, InvitationStatus, STATUS_LABELS } from '../models/invitation.model';
import { InvitationMockService } from '../services/invitation-mock.service';

@Component({
  selector: 'app-invitations-list',
  templateUrl: './invitations-list.component.html',
  styleUrls: ['./invitations-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationsListComponent implements OnInit, OnDestroy {
  rows: Invitation[] = [];
  count = 0;
  search = '';
  statusFilter: InvitationStatus | 'all' = 'all';
  showArchived = false;
  loading = false;
  showCreateModal = false;
  showEditModal = false;
  editId: string | null = null;
  // Default: sort by presentation date (soonest upcoming first). Open invitations sort
  // purely by date — status is NOT a factor between them.
  // Closed invitations are pushed to the bottom (archived are already filtered out).
  // Status only groups the list when the user explicitly clicks the Status column.
  sortKey: 'name' | 'presentation' | 'status' = 'presentation';
  sortDir: 'asc' | 'desc' = 'asc';
  page = 0;
  pageSize = 10;
  pageSizes = [5, 10, 15, 20, 25];
  statuses: Array<InvitationStatus | 'all'> = ['all', 'open', 'closed'];
  private destroy$ = new Subject<void>();

  constructor(private svc: InvitationMockService, private router: Router) {}

  ngOnInit(): void { this.reload(); }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  reload(): void {
    this.loading = true;
    this.svc.list({
      search: this.search, status: this.statusFilter, archived: this.showArchived,
      sort: { prop: this.sortKey, dir: this.sortDir }, page: this.page, pageSize: this.pageSize,
    }).pipe(takeUntil(this.destroy$))
      .subscribe(res => { this.rows = res.rows; this.count = res.count; this.loading = false; });
  }

  onFilter(): void { this.page = 0; this.reload(); }
  setPage(p: number): void { this.page = Math.max(0, Math.min(p, this.pageCount() - 1)); this.reload(); }
  pageCount(): number { return Math.max(1, Math.ceil(this.count / this.pageSize)); }
  pages(): number[] { return Array.from({ length: this.pageCount() }, (_, i) => i); }
  pageStart(): number { return this.count === 0 ? 0 : this.page * this.pageSize + 1; }
  pageEnd(): number { return Math.min((this.page + 1) * this.pageSize, this.count); }

  sortBy(key: 'name' | 'presentation' | 'status'): void {
    if (this.sortKey === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
    else { this.sortKey = key; this.sortDir = 'asc'; }
    this.page = 0;
    this.reload();
  }
  sortIcon(key: 'name' | 'presentation' | 'status'): string {
    if (this.sortKey !== key) { return 'fa-sort'; }
    return this.sortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }
  clearSearch(): void { this.search = ''; this.onFilter(); }
  statusLabel(s: InvitationStatus | 'all'): string { return s === 'all' ? 'All' : STATUS_LABELS[s]; }

  progress(i: Invitation): number {
    const scored = i.participants.filter(p => p.state === 'scored').length;
    // Clamped: scored can exceed the estimate (it's an estimate, not a cap) and an
    // unclamped width ran the bar out of its track and into the next column.
    return i.estimatedParticipants ? Math.min(100, Math.round((scored / i.estimatedParticipants) * 100)) : 0;
  }
  scoredCount(i: Invitation): number { return i.participants.filter(p => p.state === 'scored').length; }
  initials(name: string): string { return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(); }
  deadline(i: Invitation): string { return i.schedule.find(s => s.name === 'deadline')?.date ?? ''; }
  presentation(i: Invitation): string { return i.schedule.find(s => s.name === 'presentation')?.date ?? ''; }
  followupTitle(i: Invitation): string {
    if (!i.followupEmailsEnabled) { return 'Coaching emails are off'; }
    const d = i.schedule.find(s => s.name === 'followup')?.date;
    const first = d
      ? ' — first email on ' + new Date(d).toLocaleDateString('en-US')
      : '';
    return '5 coaching emails scheduled' + first;
  }
  followupDate(i: Invitation): string { return i.schedule.find(s => s.name === 'followup')?.date ?? ''; }

  /** Presentation chip colour follows the invitation status: green while open, grey otherwise. */
  presState(i: Invitation): 'open' | 'muted' {
    return i.status === 'open' ? 'open' : 'muted';
  }
  presTitle(i: Invitation): string {
    return i.status === 'open' ? 'Presentation for an open invitation' : `Presentation (invitation ${i.status})`;
  }

  open(i: Invitation): void { this.router.navigate(['/invitations', i.id]); }
  edit(i: Invitation, ev: Event): void { ev.stopPropagation(); this.editId = i.id; this.showEditModal = true; }
  create(): void { this.showCreateModal = true; }
  closeCreate(): void { this.showCreateModal = false; }
  onCreated(): void { this.showCreateModal = false; this.reload(); }
  closeEdit(): void { this.showEditModal = false; }
  onEdited(): void { this.showEditModal = false; this.reload(); }

  copyLink(i: Invitation, ev: Event): void {
    ev.stopPropagation();
    navigator.clipboard?.writeText(`${location.origin}/invitations/${i.id}`);
    Swal.fire({ title: 'Link copied', icon: 'success', timer: 1200, showConfirmButton: false });
  }

  archive(i: Invitation, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      title: `Archive "${i.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Archive',
    }).then(r => { if (r.isConfirmed) this.svc.archive(i.id, true).subscribe(() => this.reload()); });
  }

  restore(i: Invitation, ev: Event): void {
    ev.stopPropagation();
    this.svc.archive(i.id, false).subscribe(() => this.reload());
  }

  /** Empty invitations (no participants yet) can be deleted outright instead of archived. */
  isEmpty(i: Invitation): boolean { return i.participants.length === 0; }

  remove(i: Invitation, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      title: `Delete "${i.name}"?`, text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#f54359',
    }).then(r => { if (r.isConfirmed) this.svc.remove(i.id).subscribe(() => this.reload()); });
  }
}
